import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBook } from '../contexts/BookContext';
import { aiService } from '../services/aiService';
import MarkdownEditor from '../components/MarkdownEditor';
import {
  ArrowRight,
  ArrowLeft,
  Check,
  Sparkles,
  BookOpen,
  Edit,
  Download,
  Settings,
  FileText,
  Image as ImageIcon,
  Loader2,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Wand2,
  Users,
  Search,
  BookMarked
} from 'lucide-react';

/**
 * Main Create Book Page - 6-Step Wizard (KDP-Optimized)
 *
 * Step 1: Topic Discovery - Enter topic, generate magnetic titles
 * Step 2: Title Selection - Choose from 7 titles, auto-generate outline
 * Step 3: Outline Review - 12 chapters with 4-6 sub-topics (editable)
 * Step 4: Content Authoring - Write/edit chapters with AI tools + front/back matter
 * Step 5: Cover Design - Generate AI cover
 * Step 6: KDP Export - Metadata, compliance check, configure settings, export
 */
const CreateBook = () => {
  const navigate = useNavigate();
  const { state, actions, helpers } = useBook();

  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState('');
  const [activeTab, setActiveTab] = useState('chapters'); // chapters | frontMatter | backMatter | metadata
  const [generatingChapter, setGeneratingChapter] = useState(-1); // -1 = not generating, 0-11 = chapter index
  const [generateAllRunning, setGenerateAllRunning] = useState(false);
  const stopRef = useRef(false); // Ref so async loop can read stop signal

  useEffect(() => {
    setLocalError('');
  }, [state.currentStep]);

  const progress = (state.currentStep / state.totalSteps) * 100;

  // Word count helpers
  const getChapterWordCount = (chapter) => {
    return chapter?.content ? chapter.content.split(/\s+/).filter(Boolean).length : 0;
  };
  const totalWordCount = helpers.getTotalWordCount();

  // ============================================
  // STEP 1: Topic Discovery
  // ============================================

  const handleTopicSubmit = async () => {
    if (!state.originalTopic.trim()) {
      setLocalError('Please enter a topic');
      return;
    }

    setLocalLoading(true);
    try {
      const titles = await aiService.generateTitles(state.originalTopic);
      actions.setTitles(titles);
      actions.setStep(2);
    } catch (err) {
      setLocalError('Failed to generate titles. Please try again.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleTitleSelect = async (title) => {
    setLocalLoading(true);
    try {
      actions.setSelectedTitle(title);
      const chapters = await aiService.generateOutline(title, state.originalTopic);
      actions.setChapters(chapters);
      actions.setStep(3);
    } catch (err) {
      setLocalError('Failed to generate outline. Please try again.');
    } finally {
      setLocalLoading(false);
    }
  };

  // ============================================
  // STEP 3: Outline Review
  // ============================================

  const handleChapterEdit = (index, field, value) => {
    const updated = [...state.chapters];
    updated[index] = { ...updated[index], [field]: value };
    actions.setChapters(updated);
  };

  const handleSubsectionEdit = (chapterIndex, subIndex, value) => {
    const updated = [...state.chapters];
    updated[chapterIndex] = { ...updated[chapterIndex] };
    updated[chapterIndex].subsections = [...updated[chapterIndex].subsections];
    updated[chapterIndex].subsections[subIndex] = { ...updated[chapterIndex].subsections[subIndex], title: value };
    actions.setChapters(updated);
  };

  // ============================================
  // STEP 4: Content Authoring
  // ============================================

  const handleGenerateAllContent = async () => {
    setGenerateAllRunning(true);
    stopRef.current = false;
    setLocalError('');
    let failCount = 0;

    for (let i = 0; i < state.chapters.length; i++) {
      // Check stop signal
      if (stopRef.current) {
        setLocalError(`Stopped after generating ${i} chapters.`);
        break;
      }

      // Skip chapters that already have content
      if (state.chapters[i]?.content && state.chapters[i].content.trim().length > 100) {
        continue;
      }

      setGeneratingChapter(i);
      actions.setCurrentChapter(i);

      try {
        const content = await aiService.generateChapterContent(
          state.chapters[i],
          state.selectedTitle,
          state.originalTopic,
          i + 1,
          state.chapters.length
        );
        actions.setChapterContent(i, content);
      } catch (err) {
        failCount++;
        console.error(`Chapter ${i + 1} failed:`, err);
        // Continue to next chapter instead of stopping
        if (failCount >= 3) {
          setLocalError(`Generation stopped after ${failCount} failures. Try generating remaining chapters individually.`);
          break;
        }
      }

      // Small delay between chapters to avoid rate limiting
      if (i < state.chapters.length - 1) {
        await new Promise(r => setTimeout(r, 1500));
      }
    }

    setGeneratingChapter(-1);
    setGenerateAllRunning(false);

    if (failCount > 0 && failCount < 3) {
      setLocalError(`${failCount} chapter(s) failed. Use "Generate This Chapter" to retry them individually.`);
    }
  };

  const handleStopGenerateAll = () => {
    stopRef.current = true;
    setGenerateAllRunning(false);
    setGeneratingChapter(-1);
  };

  const handleGenerateChapterContent = async (chapterIndex) => {
    setLocalLoading(true);
    setGeneratingChapter(chapterIndex);
    setLocalError('');
    try {
      const content = await aiService.generateChapterContent(
        state.chapters[chapterIndex],
        state.selectedTitle,
        state.originalTopic,
        chapterIndex + 1,
        state.chapters.length
      );
      actions.setChapterContent(chapterIndex, content);
    } catch (err) {
      setLocalError(`Failed to generate Chapter ${chapterIndex + 1}. This may be a timeout issue — try again.`);
    } finally {
      setLocalLoading(false);
      setGeneratingChapter(-1);
    }
  };

  const handleExpandContent = async () => {
    const chapter = state.chapters[state.currentChapterIndex];
    if (!chapter.content) return;
    setLocalLoading(true);
    try {
      const expanded = await aiService.expandContent(chapter.content, chapter.title);
      actions.setChapterContent(state.currentChapterIndex, expanded);
    } catch (err) {
      setLocalError('Failed to expand content.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleRewriteContent = async () => {
    const chapter = state.chapters[state.currentChapterIndex];
    if (!chapter.content) return;
    setLocalLoading(true);
    try {
      const rewritten = await aiService.rewriteContent(chapter.content, chapter.title);
      actions.setChapterContent(state.currentChapterIndex, rewritten);
    } catch (err) {
      setLocalError('Failed to rewrite content.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleImproveContent = async () => {
    const chapter = state.chapters[state.currentChapterIndex];
    if (!chapter.content) return;
    setLocalLoading(true);
    try {
      const improved = await aiService.improveContent(chapter.content, chapter.title);
      actions.setChapterContent(state.currentChapterIndex, improved);
    } catch (err) {
      setLocalError('Failed to improve content.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleHumanizeContent = async () => {
    const chapter = state.chapters[state.currentChapterIndex];
    if (!chapter.content) return;
    setLocalLoading(true);
    try {
      const humanized = await aiService.humanizeContent(chapter.content, chapter.title);
      actions.setChapterContent(state.currentChapterIndex, humanized);
    } catch (err) {
      setLocalError('Failed to humanize content.');
    } finally {
      setLocalLoading(false);
    }
  };

  // Front/Back Matter Generation
  const handleGenerateFrontMatter = async (type) => {
    setLocalLoading(true);
    try {
      const content = await aiService.generateFrontMatter(
        state.selectedTitle,
        state.coverData.authorName || 'Author',
        state.originalTopic,
        type
      );
      actions.setFrontMatter({ [type]: content });
    } catch (err) {
      setLocalError(`Failed to generate ${type}.`);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleGenerateBackMatter = async (type) => {
    setLocalLoading(true);
    try {
      const content = await aiService.generateBackMatter(
        state.selectedTitle,
        state.coverData.authorName || 'Author',
        state.originalTopic,
        type
      );
      actions.setBackMatter({ [type]: content });
    } catch (err) {
      setLocalError(`Failed to generate ${type}.`);
    } finally {
      setLocalLoading(false);
    }
  };

  // ============================================
  // STEP 5: Cover Design
  // ============================================

  const handleGenerateCover = async () => {
    setLocalLoading(true);
    try {
      const result = await aiService.generateCover(
        state.selectedTitle,
        state.coverData.subtitle,
        state.coverData.authorName,
        state.coverData.stylePrompt
      );
      const imageSrc = result.imageBase64;
      actions.setCoverData({
        generatedImages: [...state.coverData.generatedImages, imageSrc],
        selectedImage: imageSrc
      });
    } catch (err) {
      setLocalError('Failed to generate cover.');
    } finally {
      setLocalLoading(false);
    }
  };

  // ============================================
  // STEP 6: KDP Export & Metadata
  // ============================================

  const handleGenerateDescription = async () => {
    setLocalLoading(true);
    try {
      const description = await aiService.generateBookDescription(
        state.selectedTitle,
        state.originalTopic,
        state.chapters
      );
      actions.setKdpMetadata({ description });
    } catch (err) {
      setLocalError('Failed to generate description.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleGenerateKeywords = async () => {
    setLocalLoading(true);
    try {
      const data = await aiService.generateKeywords(state.selectedTitle, state.originalTopic);
      actions.setKdpMetadata({
        keywords: data.keywords || [],
        categories: data.categories || [],
        targetAudience: data.targetAudience || ''
      });
    } catch (err) {
      setLocalError('Failed to generate keywords.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleComplianceCheck = async () => {
    setLocalLoading(true);
    try {
      const result = await aiService.kdpComplianceCheck(state.chapters, state.selectedTitle);
      actions.setComplianceCheck(result);
    } catch (err) {
      setLocalError('Failed to run compliance check.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleExportEpub = async () => {
    setLocalLoading(true);
    try {
      const { generateEpub } = await import('../utils/kdpExport');
      await generateEpub({
        selectedTitle: state.selectedTitle,
        chapters: state.chapters,
        coverData: state.coverData,
        kdpSettings: state.kdpSettings,
        frontMatter: state.frontMatter,
        backMatter: state.backMatter,
        kdpMetadata: state.kdpMetadata
      });
    } catch (err) {
      setLocalError('Failed to export EPUB.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleExportPdf = async () => {
    setLocalLoading(true);
    try {
      const { generateKdpPdf } = await import('../utils/kdpExport');
      const pdfBlob = await generateKdpPdf({
        selectedTitle: state.selectedTitle,
        chapters: state.chapters,
        coverData: state.coverData,
        kdpSettings: state.kdpSettings,
        frontMatter: state.frontMatter,
        backMatter: state.backMatter
      });
      const { saveAs } = await import('file-saver');
      const filename = `${state.selectedTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_interior.pdf`;
      saveAs(pdfBlob, filename);
    } catch (err) {
      setLocalError('Failed to export PDF.');
    } finally {
      setLocalLoading(false);
    }
  };

  const handleDownloadCoverSpecs = async () => {
    try {
      const { generateCoverSpecs } = await import('../utils/kdpExport');
      const specs = generateCoverSpecs({
        selectedTitle: state.selectedTitle,
        chapters: state.chapters,
        coverData: state.coverData,
        kdpSettings: state.kdpSettings,
        frontMatter: state.frontMatter,
        backMatter: state.backMatter
      });

      const specsText = `
# KDP Cover Specifications

## Book Information
Title: ${specs.title}
Author: ${specs.author}
Estimated Pages: ${specs.estimatedPages}
Trim Size: ${specs.trimSize.width}" x ${specs.trimSize.height}"

## Cover Dimensions (Full Wrap)
Total Width: ${specs.coverDimensions.width}"
Total Height: ${specs.coverDimensions.height}"
Spine Width: ${specs.spineWidth}"

### Breakdown:
- Bleed (left): ${specs.breakdown.bleedLeft}"
- Back Cover: ${specs.breakdown.backCover}"
- Spine: ${specs.breakdown.spine}"
- Front Cover: ${specs.breakdown.frontCover}"
- Bleed (right): ${specs.breakdown.bleedRight}"

## Important Requirements

### Spine Text
${specs.spineTextAllowed ? 'Allowed (79+ pages)' : 'Not allowed (under 79 pages)'}

### Barcode Space
Required: Leave ${specs.barcodeSpace} blank
- Location: Bottom right of BACK cover
- KDP will print barcode here

### Image Requirements
- Resolution: ${specs.imageRequirement}
- Format: PDF with embedded fonts
- Color: CMYK for print

Generated by AI Ebook Creator
${new Date().toLocaleString()}
      `.trim();

      const blob = new Blob([specsText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${state.selectedTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_cover_specs.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setLocalError('Failed to download cover specs.');
    }
  };

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </button>
              <div className="h-6 w-px bg-gray-300" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">AI Ebook Creator</h1>
                <p className="text-sm text-gray-500">Step {state.currentStep} of {state.totalSteps}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {state.currentStep >= 4 && (
                <div className="text-sm text-gray-500">
                  <span className={`font-mono font-semibold ${totalWordCount >= 20000 ? 'text-green-600' : totalWordCount >= 10000 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {totalWordCount.toLocaleString()}
                  </span>
                  <span className="text-gray-400"> / 20,000+ words</span>
                </div>
              )}
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {localError && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex items-start">
            <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{localError}</span>
            <button onClick={() => setLocalError('')} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
          </div>
        )}

        {/* STEP 1: Topic Discovery */}
        {state.currentStep === 1 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">What's your book about?</h2>
                <p className="text-gray-600">Enter a topic and AI will generate KDP-optimized titles</p>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Your Topic</label>
                <input
                  type="text"
                  value={state.originalTopic}
                  onChange={(e) => actions.setOriginalTopic(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleTopicSubmit()}
                  placeholder="e.g., Time management for entrepreneurs, Digital marketing strategies"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                onClick={handleTopicSubmit}
                disabled={localLoading || !state.originalTopic.trim()}
                className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {localLoading ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating titles...</>
                ) : (
                  <>Generate Magnetic Titles<ArrowRight className="w-5 h-5 ml-2" /></>
                )}
              </button>
            </div>

            <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h3 className="font-semibold text-blue-900 mb-2">KDP-Optimized Generation:</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>&#8226; 7 titles with subtitles optimized for Amazon search</li>
                <li>&#8226; 12 chapters with 4-6 subtopics each (20,000+ word target)</li>
                <li>&#8226; Front matter: Introduction, Dedication, Copyright page</li>
                <li>&#8226; Back matter: About the Author, Recommended Resources</li>
                <li>&#8226; KDP metadata: Description, keywords, categories</li>
                <li>&#8226; Humanize tool to make content pass AI detection</li>
              </ul>
            </div>
          </div>
        )}

        {/* STEP 2: Title Selection */}
        {state.currentStep === 2 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Choose Your Title</h2>
                <p className="text-gray-600">Select the title that best fits your vision (includes subtitle for KDP)</p>
              </div>
            </div>

            <div className="space-y-3">
              {state.titles.map((title, index) => (
                <button
                  key={index}
                  onClick={() => handleTitleSelect(title)}
                  disabled={localLoading}
                  className="w-full flex items-center p-4 border-2 border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-lg font-bold text-blue-600">{index + 1}</span>
                  </div>
                  <span className="ml-4 text-left font-medium text-gray-900">{title}</span>
                  {localLoading && <Loader2 className="w-5 h-5 ml-auto animate-spin text-gray-400" />}
                </button>
              ))}
            </div>

            <button
              onClick={() => actions.setStep(1)}
              className="mt-6 flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to topic input
            </button>
          </div>
        )}

        {/* STEP 3: Outline Generation */}
        {state.currentStep === 3 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Review Your Book Outline</h2>
                <p className="text-gray-600">12 chapters designed for a complete reader transformation (edit titles & subtopics below)</p>
              </div>
            </div>

            <div className="space-y-4 max-h-[500px] overflow-y-auto mb-6 pr-2">
              {state.chapters.map((chapter, index) => (
                <div key={index} className="p-4 border border-gray-200 rounded-lg hover:border-blue-200 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">Ch {index + 1}</span>
                    <input
                      type="text"
                      value={chapter.title}
                      onChange={(e) => handleChapterEdit(index, 'title', e.target.value)}
                      className="flex-1 font-semibold text-gray-900 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none pb-1"
                    />
                  </div>
                  <div className="ml-12 space-y-1">
                    {chapter.subsections.map((sub, subIndex) => (
                      <input
                        key={subIndex}
                        type="text"
                        value={sub.title}
                        onChange={(e) => handleSubsectionEdit(index, subIndex, e.target.value)}
                        className="w-full text-sm text-gray-700 border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none py-1"
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => actions.setStep(2)}
                className="flex-1 flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </button>
              <button
                onClick={() => actions.setStep(4)}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continue to Content
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 4: Content Authoring */}
        {state.currentStep === 4 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-6">
              {[
                { id: 'chapters', label: 'Chapters', icon: BookOpen },
                { id: 'frontMatter', label: 'Front Matter', icon: BookMarked },
                { id: 'backMatter', label: 'Back Matter', icon: Users },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-4 py-3 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <tab.icon className="w-4 h-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Chapters Tab */}
            {activeTab === 'chapters' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Edit className="w-5 h-5 text-blue-600" />
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">Write Your Chapters</h2>
                      <p className="text-sm text-gray-500">Target: 1,500-2,000 words per chapter (use Expand to grow)</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {generateAllRunning ? (
                      <>
                        <div className="flex items-center text-sm text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg">
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          Generating Ch {generatingChapter + 1} of {state.chapters.length}...
                        </div>
                        <button
                          onClick={handleStopGenerateAll}
                          className="flex items-center px-3 py-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 text-sm"
                        >
                          Stop
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={handleGenerateAllContent}
                        disabled={localLoading}
                        className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 transition-colors text-sm"
                      >
                        <Sparkles className="w-4 h-4 mr-2" />Generate All
                      </button>
                    )}
                  </div>
                </div>

                {/* Chapter Navigation */}
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg mb-4">
                  <button
                    onClick={() => actions.setCurrentChapter(Math.max(0, state.currentChapterIndex - 1))}
                    disabled={state.currentChapterIndex === 0}
                    className="flex items-center px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <ArrowLeft className="w-4 h-4 mr-1" />
                    Prev
                  </button>

                  <div className="text-center flex-1 mx-4">
                    <div className="font-semibold text-gray-900 text-sm">
                      Chapter {state.currentChapterIndex + 1}: {state.chapters[state.currentChapterIndex]?.title}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center justify-center gap-3">
                      <span>{state.currentChapterIndex + 1} of {state.chapters.length}</span>
                      <span className={`font-mono ${getChapterWordCount(state.chapters[state.currentChapterIndex]) >= 1500 ? 'text-green-600' : getChapterWordCount(state.chapters[state.currentChapterIndex]) >= 800 ? 'text-yellow-600' : 'text-gray-400'}`}>
                        {getChapterWordCount(state.chapters[state.currentChapterIndex]).toLocaleString()} words
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => actions.setCurrentChapter(Math.min(state.chapters.length - 1, state.currentChapterIndex + 1))}
                    disabled={state.currentChapterIndex === state.chapters.length - 1}
                    className="flex items-center px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </button>
                </div>

                {/* Chapter Quick Nav */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {state.chapters.map((ch, i) => (
                    <button
                      key={i}
                      onClick={() => actions.setCurrentChapter(i)}
                      className={`w-8 h-8 rounded text-xs font-medium transition-colors relative ${
                        i === state.currentChapterIndex
                          ? 'bg-blue-600 text-white'
                          : generatingChapter === i
                            ? 'bg-yellow-100 text-yellow-700 animate-pulse'
                            : ch.content
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                      title={`Ch ${i + 1}: ${ch.title} (${getChapterWordCount(ch)} words)`}
                    >
                      {generatingChapter === i ? <Loader2 className="w-3.5 h-3.5 animate-spin mx-auto" /> : i + 1}
                    </button>
                  ))}
                </div>

                {/* AI Action Buttons */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {!state.chapters[state.currentChapterIndex]?.content && (
                    <button
                      onClick={() => handleGenerateChapterContent(state.currentChapterIndex)}
                      disabled={localLoading}
                      className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 text-sm"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                      Generate This Chapter
                    </button>
                  )}
                  {state.chapters[state.currentChapterIndex]?.content && (
                    <>
                      <button onClick={handleExpandContent} disabled={localLoading}
                        className="flex items-center px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-50 disabled:opacity-50 text-sm">
                        <ArrowRight className="w-3.5 h-3.5 mr-1.5" />Expand
                      </button>
                      <button onClick={handleRewriteContent} disabled={localLoading}
                        className="flex items-center px-3 py-1.5 border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50 disabled:opacity-50 text-sm">
                        <Edit className="w-3.5 h-3.5 mr-1.5" />Rewrite
                      </button>
                      <button onClick={handleImproveContent} disabled={localLoading}
                        className="flex items-center px-3 py-1.5 border border-green-300 text-green-700 rounded-lg hover:bg-green-50 disabled:opacity-50 text-sm">
                        <CheckCircle className="w-3.5 h-3.5 mr-1.5" />Improve
                      </button>
                      <button onClick={handleHumanizeContent} disabled={localLoading}
                        className="flex items-center px-3 py-1.5 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 disabled:opacity-50 text-sm">
                        <Wand2 className="w-3.5 h-3.5 mr-1.5" />Humanize
                      </button>
                    </>
                  )}
                  {localLoading && <Loader2 className="w-5 h-5 animate-spin text-gray-400 ml-2 self-center" />}
                </div>

                {/* Markdown Editor */}
                <div className="mb-4" style={{ height: '500px' }}>
                  <MarkdownEditor
                    value={state.chapters[state.currentChapterIndex]?.content || ''}
                    onChange={(value) => actions.setChapterContent(state.currentChapterIndex, value)}
                    onExpand={handleExpandContent}
                    onRewrite={handleRewriteContent}
                    onImprove={handleImproveContent}
                    chapterTitle={state.chapters[state.currentChapterIndex]?.title}
                    loading={localLoading}
                    placeholder={`Start writing "${state.chapters[state.currentChapterIndex]?.title}"...\n\nClick "Generate This Chapter" or "Generate All" to let AI write it for you.`}
                  />
                </div>
              </>
            )}

            {/* Front Matter Tab */}
            {activeTab === 'frontMatter' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900">Front Matter (Required for KDP)</h2>

                {/* Dedication */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Dedication</label>
                    <button
                      onClick={() => handleGenerateFrontMatter('dedication')}
                      disabled={localLoading}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1" />Generate
                    </button>
                  </div>
                  <textarea
                    value={state.frontMatter.dedication}
                    onChange={(e) => actions.setFrontMatter({ dedication: e.target.value })}
                    placeholder="To everyone who..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Introduction */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Introduction
                      <span className="text-gray-400 ml-2 font-normal">
                        ({state.frontMatter.introduction ? state.frontMatter.introduction.split(/\s+/).filter(Boolean).length : 0} words)
                      </span>
                    </label>
                    <button
                      onClick={() => handleGenerateFrontMatter('introduction')}
                      disabled={localLoading}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {localLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      Generate
                    </button>
                  </div>
                  <textarea
                    value={state.frontMatter.introduction}
                    onChange={(e) => actions.setFrontMatter({ introduction: e.target.value })}
                    placeholder="Write or generate your book's introduction..."
                    rows={12}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>

                {/* Preface (Optional) */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Preface <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <button
                      onClick={() => handleGenerateFrontMatter('preface')}
                      disabled={localLoading}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1" />Generate
                    </button>
                  </div>
                  <textarea
                    value={state.frontMatter.preface}
                    onChange={(e) => actions.setFrontMatter({ preface: e.target.value })}
                    placeholder="Optional preface..."
                    rows={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
              </div>
            )}

            {/* Back Matter Tab */}
            {activeTab === 'backMatter' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-900">Back Matter</h2>

                {/* About the Author */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">About the Author</label>
                    <button
                      onClick={() => handleGenerateBackMatter('aboutAuthor')}
                      disabled={localLoading}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {localLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      Generate
                    </button>
                  </div>
                  <textarea
                    value={state.backMatter.aboutAuthor}
                    onChange={(e) => actions.setBackMatter({ aboutAuthor: e.target.value })}
                    placeholder="Write or generate your author bio..."
                    rows={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>

                {/* Recommended Resources */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Recommended Resources</label>
                    <button
                      onClick={() => handleGenerateBackMatter('resources')}
                      disabled={localLoading}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      <Sparkles className="w-3.5 h-3.5 mr-1" />Generate
                    </button>
                  </div>
                  <textarea
                    value={state.backMatter.resources}
                    onChange={(e) => actions.setBackMatter({ resources: e.target.value })}
                    placeholder="Recommended books, websites, tools..."
                    rows={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
              </div>
            )}

            {/* Step Navigation */}
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => actions.setStep(3)}
                className="flex-1 flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Outline
              </button>
              <button
                onClick={() => actions.setStep(5)}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continue to Cover
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 5: Cover Design */}
        {state.currentStep === 5 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <ImageIcon className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Design Your Cover</h2>
                <p className="text-gray-600">Professional cover in "Modern Business" style</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
                  <input
                    type="text"
                    value={state.selectedTitle}
                    readOnly
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subtitle (Optional)</label>
                  <input
                    type="text"
                    value={state.coverData.subtitle}
                    onChange={(e) => actions.setCoverData({ subtitle: e.target.value })}
                    placeholder="A compelling subtitle"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Author Name</label>
                  <input
                    type="text"
                    value={state.coverData.authorName}
                    onChange={(e) => actions.setCoverData({ authorName: e.target.value })}
                    placeholder="Your name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Style Prompt (Optional)</label>
                  <textarea
                    value={state.coverData.stylePrompt}
                    onChange={(e) => actions.setCoverData({ stylePrompt: e.target.value })}
                    placeholder="Additional style guidance..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <button
                  onClick={handleGenerateCover}
                  disabled={localLoading}
                  className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 transition-colors"
                >
                  {localLoading ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Generating Cover...</>
                  ) : (
                    <><Sparkles className="w-5 h-5 mr-2" />Generate Cover</>
                  )}
                </button>
              </div>

              <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg">
                {state.coverData.selectedImage ? (
                  <img src={state.coverData.selectedImage} alt="Book Cover" className="max-w-full max-h-96 shadow-lg rounded" />
                ) : (
                  <div className="text-center text-gray-400">
                    <ImageIcon className="w-16 h-16 mx-auto mb-2" />
                    <p>Your cover will appear here</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => actions.setStep(4)}
                className="flex-1 flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Content
              </button>
              <button
                onClick={() => actions.setStep(6)}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continue to Export
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 6: KDP Export & Metadata */}
        {state.currentStep === 6 && (
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Download className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Export for KDP</h2>
                <p className="text-gray-600">Configure metadata, run compliance check, and download files</p>
              </div>
            </div>

            {/* KDP Compliance Check */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                  KDP Compliance Check
                </h3>
                <button
                  onClick={handleComplianceCheck}
                  disabled={localLoading}
                  className="flex items-center px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 text-sm"
                >
                  {localLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Search className="w-4 h-4 mr-1" />Run Check</>}
                </button>
              </div>

              {state.complianceCheck && (
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-sm">
                    <span className={`px-2 py-1 rounded font-semibold ${state.complianceCheck.passed ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {state.complianceCheck.passed ? 'PASSED' : 'ISSUES FOUND'}
                    </span>
                    <span>Words: <strong>{state.complianceCheck.totalWords?.toLocaleString()}</strong></span>
                    <span>Pages: <strong>~{state.complianceCheck.estimatedPages}</strong></span>
                    <span>Chapters: <strong>{state.complianceCheck.chapterCount}</strong></span>
                  </div>

                  {state.complianceCheck.issues?.length > 0 && (
                    <div className="space-y-1">
                      {state.complianceCheck.issues.map((issue, i) => (
                        <div key={i} className="flex items-start text-sm text-red-700">
                          <XCircle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                          {issue}
                        </div>
                      ))}
                    </div>
                  )}

                  {state.complianceCheck.warnings?.length > 0 && (
                    <div className="space-y-1">
                      {state.complianceCheck.warnings.map((warning, i) => (
                        <div key={i} className="flex items-start text-sm text-yellow-700">
                          <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5" />
                          {warning}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* KDP Metadata */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-blue-600" />
                KDP Listing Metadata
              </h3>

              <div className="space-y-4">
                {/* Book Description */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Book Description (Amazon listing)
                      <span className="text-gray-400 ml-2 font-normal">
                        {state.kdpMetadata.description?.length || 0} / 4,000 chars
                      </span>
                    </label>
                    <button
                      onClick={handleGenerateDescription}
                      disabled={localLoading}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {localLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      Generate
                    </button>
                  </div>
                  <textarea
                    value={state.kdpMetadata.description}
                    onChange={(e) => actions.setKdpMetadata({ description: e.target.value })}
                    placeholder="Your Amazon book description (HTML allowed: <b>, <i>, <br>, <h2>)..."
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>

                {/* Keywords */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Keywords (7 for KDP)
                    </label>
                    <button
                      onClick={handleGenerateKeywords}
                      disabled={localLoading}
                      className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {localLoading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      Generate Keywords
                    </button>
                  </div>
                  <div className="space-y-2">
                    {(state.kdpMetadata.keywords?.length > 0 ? state.kdpMetadata.keywords : ['', '', '', '', '', '', '']).map((kw, i) => (
                      <input
                        key={i}
                        type="text"
                        value={kw}
                        onChange={(e) => {
                          const updated = [...(state.kdpMetadata.keywords || ['', '', '', '', '', '', ''])];
                          updated[i] = e.target.value;
                          actions.setKdpMetadata({ keywords: updated });
                        }}
                        placeholder={`Keyword ${i + 1} (e.g., "time management for entrepreneurs")`}
                        className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    ))}
                  </div>
                </div>

                {/* Categories */}
                {state.kdpMetadata.categories?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Suggested Categories</label>
                    <div className="flex flex-wrap gap-2">
                      {state.kdpMetadata.categories.map((cat, i) => (
                        <span key={i} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">{cat}</span>
                      ))}
                    </div>
                  </div>
                )}

                {state.kdpMetadata.targetAudience && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
                    <p className="text-sm text-gray-600">{state.kdpMetadata.targetAudience}</p>
                  </div>
                )}
              </div>
            </div>

            {/* KDP Formatting Settings */}
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="w-5 h-5 mr-2 text-blue-600" />
                KDP Formatting Settings
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Trim Size</label>
                  <select
                    value={state.kdpSettings.trimSize}
                    onChange={(e) => actions.setKdpSettings({ trimSize: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="5x8">5" x 8"</option>
                    <option value="5.25x8">5.25" x 8"</option>
                    <option value="5.5x8.5">5.5" x 8.5"</option>
                    <option value="6x9">6" x 9" (Standard)</option>
                    <option value="7x10">7" x 10"</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Paper Type</label>
                  <select
                    value={state.kdpSettings.paperType}
                    onChange={(e) => actions.setKdpSettings({ paperType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="white">White Paper</option>
                    <option value="cream">Cream Paper</option>
                    <option value="color">Color Paper</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Font Family</label>
                  <select
                    value={state.kdpSettings.fontFamily}
                    onChange={(e) => actions.setKdpSettings({ fontFamily: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Garamond">Garamond (Recommended)</option>
                    <option value="Palatino">Palatino</option>
                    <option value="Times New Roman">Times New Roman</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Font Size</label>
                  <select
                    value={state.kdpSettings.fontSize}
                    onChange={(e) => actions.setKdpSettings({ fontSize: parseInt(e.target.value) })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="10">10pt</option>
                    <option value="11">11pt (Recommended)</option>
                    <option value="12">12pt</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Pages</label>
                  <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-mono">
                    {helpers.estimatePageCount()}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Inside Margin (Gutter)</label>
                  <div className="px-4 py-2 bg-white border border-gray-300 rounded-lg font-mono">
                    {helpers.calculateKdpMargins(helpers.estimatePageCount()).gutter}"
                  </div>
                </div>
              </div>
            </div>

            {/* Export Buttons */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={handleExportEpub}
                disabled={localLoading}
                className="flex flex-col items-center justify-center p-6 border-2 border-orange-300 rounded-lg hover:bg-orange-50 transition-colors disabled:opacity-50"
              >
                <FileText className="w-8 h-8 text-orange-600 mb-2" />
                <span className="font-semibold text-gray-900">EPUB (Ebook)</span>
                <span className="text-sm text-gray-600">With front/back matter</span>
              </button>

              <button
                onClick={handleExportPdf}
                disabled={localLoading}
                className="flex flex-col items-center justify-center p-6 border-2 border-blue-300 rounded-lg hover:bg-blue-50 transition-colors disabled:opacity-50"
              >
                <Download className="w-8 h-8 text-blue-600 mb-2" />
                <span className="font-semibold text-gray-900">PDF (Paperback)</span>
                <span className="text-sm text-gray-600">Print-ready interior</span>
              </button>

              <button
                onClick={handleDownloadCoverSpecs}
                className="flex flex-col items-center justify-center p-6 border-2 border-green-300 rounded-lg hover:bg-green-50 transition-colors"
              >
                <Settings className="w-8 h-8 text-green-600 mb-2" />
                <span className="font-semibold text-gray-900">Cover Specs</span>
                <span className="text-sm text-gray-600">KDP cover requirements</span>
              </button>
            </div>

            {localLoading && (
              <div className="mt-6 flex items-center justify-center p-4 bg-gray-50 rounded-lg">
                <Loader2 className="w-6 h-6 mr-2 animate-spin text-blue-600" />
                <span className="text-gray-700">Generating export...</span>
              </div>
            )}

            <div className="flex gap-4 mt-6">
              <button
                onClick={() => actions.setStep(5)}
                className="flex-1 flex items-center justify-center px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Cover
              </button>
              <button
                onClick={() => {
                  actions.resetProject();
                  navigate('/');
                }}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Check className="w-5 h-5 mr-2" />
                Complete & Start New
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CreateBook;
